import { useCallback, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { generatePageUrl } from '@/app/pages';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { UiButton, UiHStack, UiIconCheck, UiIconPencilSimple, UiStack, UiText } from '@/lib/ui';
import { useQuery } from '@tanstack/react-query';
import {
  loadRegistrationInfoFormResponse,
  type InfoFormResponse,
  InfoFormType,
  type InfoForm,
  type InfoFormByAttendeeCategoryQuery,
  infoFormByAttendeeCategoryQueryKey,
  loadInfoFormByAttendeeCategory,
  infoFormResponseQueryKeyWithStupidItem,
} from '@/api/registration';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import InformationList from './InformationList';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import BaseButtonPlainIconEdit from '@/base/Button/PlainIconEdit';
import BaseButtonIconText from '@/base/Button/IconText';

const PersonalInfoReview: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantCode, eventId } = useRegisterRoute();
  const registrationQuery = useRegistrationQuery(eventId);
  const registration = registrationQuery?.data;
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();

  const infoFormByAttendeeCategoryQuery = useQuery<InfoForm, Error>(
    [infoFormByAttendeeCategoryQueryKey, { registrationId: registration?.id, formType: InfoFormType.PERSONAL }],
    async () => { return await fetchInfoFormByAttendeeCategory(registration?.id); },
    {
      enabled: !!registration && !isApiPreparing
    }
  );

  async function fetchInfoFormByAttendeeCategory(registrationId: number | undefined): Promise<InfoForm> {
    if (typeof registrationId === 'undefined') {
      return await Promise.reject(new Error('Invalid registrationId'));
    }

    const params: InfoFormByAttendeeCategoryQuery = { formType: InfoFormType.PERSONAL, registrationId };
    const result = await loadInfoFormByAttendeeCategory(createTenantApiRequest)(params);

    return result.item ?? null;
  }

  /**
   * infoFormResponseQueryKeyWithStupidItem is named in the way that, there were previous pages that load the same form response info
   * but didn't return the item instead returned the whole object. We need a separate key to avoid using the previous whatever form response info using the query key
   * 'infoFormResponseQueryKey'.
   */
  const personalInfoQuery = useQuery<InfoFormResponse, Error>(
    [infoFormResponseQueryKeyWithStupidItem, { registrationId: registration?.id, type: InfoFormType.PERSONAL }],
    async () => { return await fetchRegistrationInfoFormResponse(registration?.id); },
    {
      enabled: !!registration && !isApiPreparing && infoFormByAttendeeCategoryQuery.isSuccess
    }
  );

  async function fetchRegistrationInfoFormResponse(registrationId: number | undefined): Promise<InfoFormResponse> {
    if (typeof registrationId === 'undefined') {
      return await Promise.reject(new Error('Invalid registrationId'));
    }

    const response = await loadRegistrationInfoFormResponse(createTenantApiRequest)({
      registrationId,
      type: InfoFormType.PERSONAL
    });

    return response.item;
  }

  const toModify = useCallback(
    () => {
      navigate(generatePageUrl(
        'RegistrationRegisterPersonalInfo',
        { tenantCode, eventId },
        { ...Object.fromEntries(searchParams), review: true }
      ));
    },
    [eventId, navigate, searchParams, tenantCode]
  );

  return (
    <UiStack spacing={4}>
      <UiHStack spacing={4}>
        <UiText variant={'body1'} fontWeight={600}>
          Personal information
        </UiText>
        <BaseButtonIconText
          Icon={UiIconPencilSimple}
          onClick={toModify}
        >
          Edit
        </BaseButtonIconText>
        {/*<BaseButtonPlainIconEdit onClick={toModify}/>*/}
      </UiHStack>
      <QueryContainer query={infoFormByAttendeeCategoryQuery}>
        {(infoForm) => {
          return (
            <QueryContainer query={personalInfoQuery}>
              {(personalInfoForm) => {

                // console.log('----personal info form', personalInfoForm, infoForm);
                return (
                  <InformationList
                    fieldsMetadata={infoForm.config.fieldsMetadata}
                    response={personalInfoForm.response}
                  />
                );
              }}
            </QueryContainer>
          );
        }}
      </QueryContainer>
    </UiStack>
  );
};

export default PersonalInfoReview;
